import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import AdminLayout from './AdminLayout';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Calendar, Filter, LayoutDashboard, FileText, AlertTriangle, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AlphaFeatureGates {
  clientsEnabled: boolean;
  tripsEnabled: boolean;
  pipelineEnabled: boolean;
  dashboardEnabled: boolean;
  proposalsEnabled: boolean;
}

const FEATURE_CONFIG = [
  {
    key: 'clientsEnabled' as const,
    label: 'Clients (CRM)',
    description: 'Client contact management and CRM features',
    icon: Users,
  },
  {
    key: 'tripsEnabled' as const,
    label: 'Trips',
    description: 'Trip planning and itinerary management',
    icon: Calendar,
  },
  {
    key: 'pipelineEnabled' as const,
    label: 'Pipeline',
    description: 'Sales pipeline and opportunity tracking',
    icon: Filter,
  },
  {
    key: 'dashboardEnabled' as const,
    label: 'Dashboard',
    description: 'Main dashboard with analytics and overview',
    icon: LayoutDashboard,
  },
  {
    key: 'proposalsEnabled' as const,
    label: 'Proposals',
    description: 'Client proposal creation and management',
    icon: FileText,
  },
];

const AdminFeatureGatesPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch current feature gates configuration
  const { data: gates, isLoading, error } = useQuery<AlphaFeatureGates>({
    queryKey: ['/api/admin/feature-gates'],
    queryFn: () => apiRequest('GET', '/api/admin/feature-gates'),
    staleTime: 30 * 1000, // 30 seconds
  });

  // Mutation to update feature gates
  const updateGatesMutation = useMutation({
    mutationFn: (updates: Partial<AlphaFeatureGates>) =>
      apiRequest('PATCH', '/api/admin/feature-gates', updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/feature-gates'] });
      // Also invalidate the public feature-gates endpoint so all clients get the update
      queryClient.invalidateQueries({ queryKey: ['/api/feature-gates'] });
      toast({
        title: 'Feature gates updated',
        description: 'Changes have been saved successfully.',
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update feature gates.',
        variant: 'destructive',
        duration: 5000,
      });
    },
  });

  const handleToggle = (key: keyof AlphaFeatureGates, newValue: boolean) => {
    updateGatesMutation.mutate({ [key]: newValue });
  };

  if (isLoading) {
    return (
      <AdminLayout
        title="Alpha Feature Gates"
        description="Control which features are available to non-admin users during alpha testing."
      >
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout
        title="Alpha Feature Gates"
        description="Control which features are available to non-admin users during alpha testing."
      >
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Failed to load feature gates</h3>
              <p className="text-sm text-red-700">
                {(error as any).message || 'An error occurred while loading the configuration.'}
              </p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Alpha Feature Gates"
      description="Control which features are available to non-admin users during alpha testing. Admin and System Admin users always have full access regardless of these settings."
    >
      {/* Info Banner */}
      <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 mt-0.5 text-orange-600 dark:text-orange-400" />
          <div className="text-sm text-orange-800 dark:text-orange-200">
            <p className="font-medium">About Alpha Feature Gates</p>
            <p className="mt-1 text-orange-700 dark:text-orange-300">
              When a feature is <strong>disabled</strong>, non-admin users will see it greyed out in the sidebar
              with a "Coming soon..." message. Direct navigation to disabled features will redirect users
              back to Threads. <strong>Admin and System Admin users are never affected</strong> by these gates.
            </p>
          </div>
        </div>
      </div>

      {/* Feature Gates List */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-slate-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Feature Access Control</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Enable or disable features for standard advisor users.</p>
        </div>

        <div className="divide-y divide-slate-200 dark:divide-gray-700">
          {FEATURE_CONFIG.map((feature) => {
            const Icon = feature.icon;
            const isEnabled = gates?.[feature.key] ?? false;

            return (
              <div
                key={feature.key}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-4">
                  <div className={`rounded-lg p-2 ${isEnabled ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-slate-100 dark:bg-gray-700'}`}>
                    <Icon className={`h-5 w-5 ${isEnabled ? 'text-orange-600 dark:text-orange-400' : 'text-slate-400 dark:text-slate-500'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-medium text-slate-900 dark:text-slate-100">
                        {feature.label}
                      </Label>
                      <Badge
                        variant={isEnabled ? 'default' : 'secondary'}
                        className={`text-xs ${isEnabled ? 'bg-orange-600' : 'bg-slate-200 text-slate-600 dark:bg-gray-600 dark:text-slate-300'}`}
                      >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p id={`gate-desc-${feature.key}`} className="text-sm text-slate-500 dark:text-slate-400">{feature.description}</p>
                  </div>
                </div>

                {/* AR-210 (TC-38.2): the five switches were unnamed ('switch, off' x5).
                    aria-label rather than <Label htmlFor>, so clicking the text can
                    never flip a gate by accident. */}
                <Switch
                  aria-label={feature.label}
                  aria-describedby={`gate-desc-${feature.key}`}
                  checked={isEnabled}
                  onCheckedChange={(checked) => handleToggle(feature.key, checked)}
                  disabled={updateGatesMutation.isPending}
                  className="data-[state=checked]:bg-orange-600"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={() => {
            updateGatesMutation.mutate({
              clientsEnabled: true,
              tripsEnabled: true,
              pipelineEnabled: true,
              dashboardEnabled: true,
              proposalsEnabled: true,
            });
          }}
          disabled={updateGatesMutation.isPending}
          className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-300 dark:hover:bg-orange-900/50"
        >
          Enable All
        </button>
        <button
          onClick={() => {
            updateGatesMutation.mutate({
              clientsEnabled: false,
              tripsEnabled: false,
              pipelineEnabled: false,
              dashboardEnabled: false,
              proposalsEnabled: false,
            });
          }}
          disabled={updateGatesMutation.isPending}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600"
        >
          Disable All
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminFeatureGatesPage;
